<script setup lang="ts">
import { computed, ref, useSlots, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import HelpIcon from '@/assets/icons/help.svg?component'
import { BoxComponent, FlexComponent, TooltipComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  hasDynamicUrls?: boolean
  stickyHeader?: boolean
  paddings?: number | string
  tabs: {
    label: string
    path?: string
    tooltipSlotName?: string
  }[]
}>()
const slots = useSlots()
const route = useRoute()
const router = useRouter()

const activeTab = ref()

const tabSlots = computed(() => {
  if (!slots) {
    return []
  }
  return Object.keys(slots).filter((slot: string) => slot.includes('tab'))
})

const currentTabParam = computed(() => {
  const tab = route.params.tab
  return Array.isArray(tab) ? tab[0] : tab
})

watch(
  currentTabParam,
  (tab) => {
    if (props.hasDynamicUrls) {
      const index = tab ? props.tabs.findIndex((item) => item.path === tab) : -1
      activeTab.value = index !== -1 ? index : 0
    } else {
      activeTab.value = 0
    }
  },
  { immediate: true },
)
watch(activeTab, async (newValue) => {
  const path = props.tabs[newValue]?.path
  if (props.hasDynamicUrls && path && currentTabParam.value !== path) {
    await router.push({ params: { ...route.params, tab: path } })
  }
})
</script>

<template>
  <div class="tabs">
    <FlexComponent :gap="16" class="tabs__head">
      <button
        v-for="(tab, index) in tabs"
        :key="tab.label"
        type="button"
        :class="['tabs__head__item', { 'is-active': activeTab === index }]"
        @click="() => (activeTab = index)"
      >
        <FlexComponent :gap="8" align="center">
          <TypographyComponent variant="title-large">
            {{ tab.label }}
          </TypographyComponent>
          <TooltipComponent
            v-if="tab.tooltipSlotName && slots[tab.tooltipSlotName]"
            placement="bottom"
            effect="light"
            :show-arrow="false"
            class="tabs__tooltip"
          >
            <el-icon :size="18" class="tooltip-handle">
              <HelpIcon />
            </el-icon>
            <template #content>
              <slot :name="tab.tooltipSlotName" />
            </template>
          </TooltipComponent>
        </FlexComponent>
      </button>
    </FlexComponent>
    <BoxComponent class="tabs__content" :p="props.paddings" :pt="24">
      <BoxComponent
        v-for="(slotName, index) in tabSlots"
        :key="slotName"
        :class="['tabs__content__item', { 'is-active': activeTab === index }]"
      >
        <slot :name="slotName" />
      </BoxComponent>
    </BoxComponent>
  </div>
</template>

<style scoped lang="scss">
.tabs__head__item {
  padding: 10px 24px;
  border-radius: 40px;
  cursor: pointer;
  transition: all var(--transition) ease;
  color: var(--black);
  background: var(--white);
  border: none;
  font-weight: 600;

  &.is-active {
    background: var(--button-hover);
  }
}

.tabs__content {
  width: 100%;
  overflow: hidden;
}

.tabs__content__item {
  display: none;

  &.is-active {
    display: block;
    min-width: 100%;
  }
}

.tabs__tooltip {
  display: flex;
}
</style>
